import { Link } from "react-router-dom";
import type { LeagueStandings } from "@liga/shared";

interface Props {
  standings: LeagueStandings;
}

// Podium colors for the top three positions; everyone else gets a neutral chip.
function medalClass(pos: number): string {
  if (pos === 1) return "bg-[#d4af37] text-white";
  if (pos === 2) return "bg-[#c3c7cf] text-white";
  if (pos === 3) return "bg-[#c0894e] text-white";
  return "bg-[#efece6] text-[#7a7263]";
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

const GRID = "grid grid-cols-[46px_1fr_84px_92px] items-center";

export function LeagueStandingsTable({ standings }: Props) {
  const { league, rows } = standings;
  const monthsLabel =
    league.durationMonths === 1 ? "Mês único" : `${league.durationMonths} meses`;
  const etapasLabel = league.etapasPerMonth
    ? `${league.etapasPerMonth.reduce((s, n) => s + n, 0)} etapas (${league.etapasPerMonth.join("+")})`
    : null;

  return (
    <section className="rounded-2xl border border-line bg-white shadow-sm overflow-hidden hover:border-brand-300 transition-colors">
      <Link
        to={`/leagues/${league.id}`}
        className="block px-5 pt-5 pb-4 border-b border-[#efeae0] hover:bg-[#fcf9f2] transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-xl font-bold tracking-tight text-ink">
            {league.name}
          </h2>
          <div className="flex-none flex flex-col items-end gap-1">
            <span className="text-xs font-semibold text-[#6b6356] bg-brand-50 rounded-full px-3 py-1 whitespace-nowrap">
              {monthsLabel}
            </span>
            {etapasLabel && (
              <span className="text-[11px] font-semibold text-[#8a8170] whitespace-nowrap">
                {etapasLabel}
              </span>
            )}
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 text-[13px]">
          <span className="text-[#8a8170]">
            {rows.length} {rows.length === 1 ? "jogador" : "jogadores"}
          </span>
          <span className="font-semibold text-brand-700">
            Ver detalhes →
          </span>
        </div>
      </Link>

      <div
        className={`${GRID} px-5 py-3 text-[11px] uppercase tracking-wide text-[#a39a87] font-bold border-b border-[#efeae0]`}
      >
        <span>#</span>
        <span>Jogador</span>
        <span className="text-right">Média</span>
        <span className="text-right">Pontos</span>
      </div>

      {rows.length === 0 && (
        <div className="text-center text-slate-500 py-8">
          Sem resultados para esta liga ainda.
        </div>
      )}

      {rows.map((row, idx) => {
        const pos = idx + 1;
        const top = pos <= 3;
        return (
          <div
            key={row.playerId}
            className={[
              GRID,
              "px-5 py-[14px] border-b border-[#f3efe6] last:border-b-0",
              top ? "bg-[#fcf9f2]" : "bg-white",
            ].join(" ")}
          >
            <div>
              <span
                className={[
                  "inline-flex items-center justify-center min-w-[27px] h-[27px] rounded-lg text-[13px] font-bold font-display",
                  medalClass(pos),
                ].join(" ")}
              >
                {pos}
              </span>
            </div>
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex-none w-[30px] h-[30px] rounded-full bg-[#efece6] text-[#7a7263] flex items-center justify-center text-[11px] font-bold">
                {initials(row.playerName)}
              </span>
              <span
                className={[
                  "text-[15px] truncate",
                  top ? "font-bold text-ink" : "font-medium text-ink",
                ].join(" ")}
              >
                {row.playerName}
              </span>
            </div>
            <span className="text-right text-sm text-[#6b6356] tabular-nums">
              {row.avgRank.toFixed(2)}
            </span>
            <span className="text-right text-[17px] font-bold font-display tabular-nums text-ink">
              {row.points}
            </span>
          </div>
        );
      })}
    </section>
  );
}
